/**
 * Permission-based authorization hooks for RBAC
 *
 * These hooks provide declarative permission and role checks throughout the UI.
 * All checks honor the super admin bypass (super admins have wildcard permissions).
 *
 * Usage examples:
 *
 * // Check single permission
 * const canViewUsers = useHasPermission('users.view')
 *
 * // Check multiple permissions with OR logic (default)
 * const canManageUsers = useHasPermission(['users.manage', 'users.create'])
 *
 * // Check multiple permissions with AND logic
 * const canFullAccess = useHasPermission(['users.view', 'users.manage'], { requireAll: true })
 *
 * // Check role
 * const isAdmin = useHasRole('admin')
 *
 * // Check multiple roles with OR logic
 * const isStaff = useHasRole(['admin', 'user'])
 */

import { useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'

export interface PermissionCheckOptions {
  /**
   * When checking multiple permissions:
   * - true: require ALL permissions (AND logic)
   * - false: require ANY permission (OR logic, default)
   */
  requireAll?: boolean
}

/**
 * Hook to check if the current user has specific permission(s)
 *
 * Super admins automatically have all permissions (bypass).
 *
 * @param permissionCode - Single permission code or array of codes
 * @param options - Options for permission checking (requireAll for AND logic)
 * @returns true if user has the required permission(s), false otherwise
 */
export function useHasPermission(
  permissionCode: string | string[],
  options: PermissionCheckOptions = {}
): boolean {
  const { user } = useAuthStore()
  const { requireAll = false } = options

  return useMemo(() => {
    // No user = no permissions
    if (!user) return false

    // Super admin bypass - has all permissions
    if (user.isSuperAdmin) return true

    // Get user's permissions (default to empty array if not set)
    const userPermissions = user.permissions || []

    // Single permission check
    if (typeof permissionCode === 'string') {
      return userPermissions.includes(permissionCode)
    }

    // Multiple permissions check
    if (Array.isArray(permissionCode)) {
      if (requireAll) {
        // AND logic: user must have ALL permissions
        return permissionCode.every(code => userPermissions.includes(code))
      } else {
        // OR logic: user must have AT LEAST ONE permission
        return permissionCode.some(code => userPermissions.includes(code))
      }
    }

    return false
  }, [user, permissionCode, requireAll])
}

/**
 * Hook to check if the current user has specific role(s)
 *
 * Super admins automatically pass all role checks (bypass).
 *
 * @param roleSlug - Single role slug or array of role slugs
 * @returns true if user has any of the specified roles, false otherwise
 */
export function useHasRole(roleSlug: string | string[]): boolean {
  const { user } = useAuthStore()

  return useMemo(() => {
    // No user = no roles
    if (!user) return false

    // Super admin bypass - considered to have all roles
    if (user.isSuperAdmin) return true

    // Get user's role slugs (default to empty array if not set)
    const userRoles = (user.roles || []).map(role => role.slug)

    // Single role check
    if (typeof roleSlug === 'string') {
      return userRoles.includes(roleSlug)
    }

    // Multiple roles check (OR logic - has ANY of the roles)
    if (Array.isArray(roleSlug)) {
      return roleSlug.some(slug => userRoles.includes(slug))
    }

    return false
  }, [user, roleSlug])
}

/**
 * Hook to check if current user is a super admin
 *
 * @returns true if user is super admin, false otherwise
 */
export function useIsSuperAdmin(): boolean {
  const { user } = useAuthStore()
  return useMemo(() => user?.isSuperAdmin || false, [user])
}

/**
 * Hook to get all user permissions (for debugging/display)
 *
 * @returns array of permission codes the user has
 */
export function useUserPermissions(): string[] {
  const { user } = useAuthStore()
  return useMemo(() => {
    if (!user) return []
    if (user.isSuperAdmin) return ['*'] // Wildcard for super admin
    return user.permissions || []
  }, [user])
}

/**
 * Hook to get all user roles (for debugging/display)
 *
 * @returns array of role objects the user has
 */
export function useUserRoles() {
  const { user } = useAuthStore()
  return useMemo(() => user?.roles || [], [user])
}
